import {
  GraphicComponentTypes,
  IGraphic,
  GraphicComponent,
  IGraphicsControllerStoreState,
  IGraphicSetting,
  PlacementTypes,
  IGraphicUGCStoreItem,
  IGraphicUGCItem,
  IZoomGraphicUGCItem
} from "@/libs/graphics/types/Graphics.t";

/**
 * The Graphics Controller getters interface. Receives the state and returns getter functions
 */
export type GettersGraphicsController = {
  /**
   * Returns the graphic currently applied to 3D component supplied as argument
   * @param component3D the 3D component type (graphic_front, graphic_back etc)
   */
  getGraphicByComponent3D(
    state: IGraphicsControllerStoreState,
    getters: GettersGraphicsController,
    rootState: any
  ): (component3D: PlacementTypes) => IGraphic | null;

  /**
   * Returns the graphic currently applied to 3D component supplied as argument
   * @param component3D the 3D component type
   * @param componentType the graphic component type
   */
  getGraphicComponent(
    state: IGraphicsControllerStoreState,
    getters: GettersGraphicsController,
    rootState: any
  ): (
      component3D: PlacementTypes,
      componentType: GraphicComponentTypes
    ) => GraphicComponent;

  /**
   * Returns the graphic package data
   * @param graphicID the graphic package identifier
   */
  getGraphicSettings(
    state: IGraphicsControllerStoreState
  ): (graphicID: string) => IGraphicSetting | undefined;

  /**
   * This function returns the graphic UGC item from the store that matches the placement and graphic ID.
   * @param {string: PlacementTypes} placement
   * @param {string} graphicID
   */
  getGraphicUGCSetting(
    state: IGraphicsControllerStoreState,
    getters: any
  ): (
      placement: PlacementTypes,
      graphicID: number
    ) => IGraphicUGCItem | undefined;

  getGraphicUGCStoreItem(state: IGraphicsControllerStoreState): (
    placement: PlacementTypes
  ) => IGraphicUGCStoreItem | undefined

  getOriginalGraphicViewBox(state: any,
     getters: any,
     rootState: any,
     rootGetters: any): (
  ) => IZoomGraphicUGCItem | undefined
};

const getters: GettersGraphicsController = {
  getGraphicByComponent3D: (rootState: any) => (
    component3D: PlacementTypes
  ): IGraphic | null => {
    const componentObj =
      rootState.recipe.recipe_v2.components &&
      rootState.recipe.recipe_v2.components[component3D];
    const isGraphics = componentObj && componentObj.group === "graphics";
    if (!isGraphics) return null;
    const graphic: IGraphic = rootState.gdm.data.find(
      (swatch: IGraphic) => swatch.id === componentObj.swatch_id
    );
    return graphic;
  },

  getGraphicComponent: (rootState: any) => (
    component3D,
    componentType
  ): GraphicComponent => {
    const graphic: IGraphic = (rootState.recipe.recipe_v2
      .graphics as IGraphic[]).find(
        (graphic) => graphic.component3D === component3D
      ) as IGraphic;
    return graphic.component.find(
      (component) => component.id === componentType
    ) as GraphicComponent;
  },

  getGraphicSettings: (state: IGraphicsControllerStoreState) => (
    graphicID
  ): IGraphicSetting | undefined => {
    return state.graphicSetting.find((data) => data.id === graphicID);
  },

  getGraphicUGCStoreItem: (state: IGraphicsControllerStoreState) => (
    placement
  ) => {
    return state.graphicUGCSetting.find(
      (graphicUgcStoreItem: IGraphicUGCStoreItem) =>
        graphicUgcStoreItem.placement === placement
    );
  },
  getGraphicUGCSetting: (state: IGraphicsControllerStoreState, getters) => (
    placement,
    graphicID
  ): IGraphicUGCItem | undefined => {
    return getters.getGraphicUGCStoreItem(placement)?.graphics?.find((graphic: IGraphicUGCItem) => graphic.id === graphicID)
  },
  getOriginalGraphicViewBox: (state: any, getters: any,rootState: any, rootGetters: any) => (): IZoomGraphicUGCItem | undefined => {
    const { activeComponent } = rootState.standardNavigation;
    const graphic = rootGetters["gdm/getGraphicByComponent"](activeComponent);
    return rootState.graphicsController?.originalGraphicViewbox?.[activeComponent]?.[graphic?.id];
  }
};

export default getters;
